import React, { useEffect, useRef, useState } from "react";
import scrollToTop from "../helpers/scrollToTop";
import BookingButton from "../components/BookingButton";
import bannerImage from "../images/banner-image.jpg";
import { servicesData } from "../data/services";
import {
  HAIR_LENGTHS,
  getLengthPrices,
  getPriceSummary,
  groupServices,
  hasLengthPrices,
} from "../utils/servicePrice";

import { slugify } from "../utils/slugify";

// Mesmas colunas no cabeçalho e nas linhas, para os preços ficarem alinhados.
const COLUNAS_COMPRIMENTO = "sm:grid-cols-[repeat(4,5.75rem)] sm:gap-x-2";

const ValorPreco = ({ value, numeric, prefix, peso = "font-semibold" }) => (
  <span className="whitespace-nowrap">
    {prefix && (
      <span className="mr-1 text-xs font-light text-[#5c7160]/60">{prefix}</span>
    )}
    <span
      className={
        numeric
          ? `${peso} text-[#c0a080] tabular-nums`
          : "text-sm italic font-light text-[#5c7160]/60"
      }
    >
      {value}
    </span>
  </span>
);

const NomeServico = ({ name, duration }) => (
  <span className="min-w-0 flex-1 font-light text-[#5c7160]">
    {name}
    {duration ? (
      <span className="block text-xs text-[#5c7160]/60 mt-0.5">{duration} min</span>
    ) : null}
  </span>
);

const LINHA = "py-2.5 border-b border-[#5c7160]/10 last:border-0 last:pb-0";

const LinhaServico = ({ svc }) => (
  <li className={`flex items-baseline gap-4 ${LINHA}`}>
    <NomeServico name={svc.name} duration={svc.duration} />
    <span className="flex-shrink-0">
      <ValorPreco {...getPriceSummary(svc)} />
    </span>
  </li>
);

// No telemóvel os quatro preços ficam numa grelha 2×2 com a etiqueta por cima;
// a partir de `sm` alinham-se em colunas debaixo do cabeçalho do grupo.
const LinhaPorComprimento = ({ svc }) => (
  <li className={`sm:flex sm:items-baseline sm:gap-4 ${LINHA}`}>
    <NomeServico name={svc.name} duration={svc.duration} />
    <div
      className={`grid grid-cols-2 gap-x-4 gap-y-2 mt-2 sm:mt-0 sm:flex-shrink-0 sm:gap-y-0 ${COLUNAS_COMPRIMENTO}`}
    >
      {getLengthPrices(svc).map(({ key, label, value, numeric }) => (
        <span key={key} className="flex flex-col sm:block sm:text-right">
          <span className="text-[10px] uppercase tracking-wider text-[#5c7160]/50 sm:hidden">
            {label}
          </span>
          <ValorPreco value={value} numeric={numeric} peso="font-medium" />
        </span>
      ))}
    </div>
  </li>
);

function Prices() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("");

  const chipsRef = useRef(null);

  // scroll para topo ao montar
  useEffect(() => {
    scrollToTop();
  }, []);

  // load the local fallback service catalog
  useEffect(() => {
    try {
      setLoading(true);
      setServices(servicesData);

      const uniqueCats = [
        ...new Set(servicesData.map((s) => s.category)),
      ];
      setCategories(uniqueCats);
    } catch (e) {
      console.error(e);
      setError("Erro ao carregar o preçário. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Marca na barra a categoria que está a ser lida. Sem isto, a meio de uma
  // lista de 60 linhas não há forma de saber em que secção se está.
  useEffect(() => {
    if (loading || error || categories.length < 2) return;

    const seccoes = categories
      .map((cat) => document.getElementById(`cat-${slugify(cat)}`))
      .filter(Boolean);
    if (!seccoes.length) return;

    const visiveis = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visiveis.add(entry.target.id);
          else visiveis.delete(entry.target.id);
        });

        // A faixa observada começa logo abaixo da barra: a categoria ativa é
        // a primeira (em ordem de página) que ainda lá esteja.
        const atual = seccoes.find((el) => visiveis.has(el.id));
        if (atual) setActiveCat(atual.id);
      },
      { rootMargin: "-170px 0px -70% 0px", threshold: 0 }
    );

    seccoes.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, error, categories]);

  // Traz o chip ativo para o meio da barra, como na página de Serviços.
  useEffect(() => {
    const cont = chipsRef.current;
    if (!cont || !activeCat) return;

    const chip = cont.querySelector(`[data-cat="${activeCat}"]`);
    if (!chip) return;

    cont.scrollTo({
      left: chip.offsetLeft - cont.clientWidth / 2 + chip.clientWidth / 2,
      behavior: "smooth",
    });
  }, [activeCat]);

  return (
    <div className="min-h-screen bg-[#F5F1E9]">
      {/* Hero Section */}
      <div className="relative">
        <img
          className="object-cover h-[22vh] min-h-[140px] sm:h-[26vh] md:h-[30vh] w-full"
          src={bannerImage}
          alt="Preçário"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#c0a080]/40 to-[#c0a080]/70"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-[#F5F1E9] text-center px-6 text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-balance tracking-wide">
            Preçário
          </h2>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-16">
          <svg
            viewBox="0 0 1200 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <path
              d="M0,60 V30 C150,50 350,40 500,35 C650,30 750,25 900,30 C1050,35 1200,45 1200,45 V60 H0 Z"
              fill="#F5F1E9"
            />
          </svg>
        </div>
      </div>

      {/* Intro */}
      <section className="py-16 px-6 reveal">
        <div className="container mx-auto max-w-3xl text-center">
          <h3 className="text-3xl md:text-4xl font-light text-[#5c7160] mb-8 relative inline-block">
            Os nossos serviços e preços
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-[#a5bf99]"></span>
          </h3>
          <p className="text-lg font-light text-[#5c7160] leading-relaxed">
            Oferecemos uma variedade de tratamentos de beleza de alta qualidade
            por preços acessíveis. Consulte a nossa lista completa abaixo.
          </p>
        </div>
      </section>

      {/* Atalhos por categoria: a lista tem 60 linhas, sem isto obriga a
          percorrer a página toda no telemóvel */}
      {!loading && !error && categories.length > 1 && (
        <nav
          aria-label="Categorias"
          className="sticky top-24 z-30 bg-[#F5F1E9] border-b border-[#5c7160]/10"
        >
          {/* A navbar principal desaparece ao descer a página e deixa 96px
              destapados por cima desta barra; sem isto o preçário passa por
              trás dela e parece que os dois textos se sobrepõem. */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-full h-24 bg-[#F5F1E9]"
          />
          <div className="relative container mx-auto max-w-5xl">
            <div
              ref={chipsRef}
              className="flex gap-3 overflow-x-auto hide-scrollbar px-6 py-3"
            >
              {categories.map((cat) => {
                const id = `cat-${slugify(cat)}`;
                const ativa = activeCat === id;
                return (
                  <a
                    key={cat}
                    data-cat={id}
                    href={`#${id}`}
                    aria-current={ativa ? "true" : undefined}
                    className={`flex-shrink-0 px-4 py-2 text-sm rounded-full transition-all ${
                      ativa
                        ? "bg-[#5c7160] text-white shadow-md"
                        : "bg-white text-[#5c7160] hover:bg-[#a5bf99]/20"
                    }`}
                  >
                    {cat}
                  </a>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Prices */}
      <section className="pb-16 md:pb-20 px-6 pt-10">
        <div className="container mx-auto max-w-3xl space-y-8">
          {loading && (
            <p className="text-center text-[#5c7160]">Carregando...</p>
          )}
          {error && (
            <p className="text-center text-red-500">{error}</p>
          )}

          {!loading &&
            !error &&
            categories.map((cat) => {
              // filtra serviços desta categoria
              const list = services.filter(
                (s) => s.category === cat
              );
              return (
                <div
                  key={cat}
                  id={`cat-${slugify(cat)}`}
                  className="scroll-mt-44 reveal bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-stone-200/60"
                >
                  <div className="flex items-baseline gap-3 mb-5">
                    <h4 className="text-xl md:text-2xl font-light text-[#5c7160]">
                      {cat}
                    </h4>
                    <span className="flex-1 h-px bg-gradient-to-r from-[#c0a080]/50 to-transparent" />
                  </div>
                  <div className="space-y-8">
                    {groupServices(list).map(({ nome, itens }) => {
                      const temComprimentos = itens.some(hasLengthPrices);
                      // Num grupo de preço fixo dentro de uma categoria que tem
                      // preços por comprimento, convém dizer que aqui não variam.
                      const precoUnico =
                        !temComprimentos && list.some(hasLengthPrices);

                      return (
                        <div key={nome || "_"}>
                          {(nome || temComprimentos) && (
                            <div className="flex items-end gap-4 pb-2 mb-1 border-b border-[#c0a080]/30">
                              <div className="min-w-0 flex-1">
                                {nome && (
                                  <h5 className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#5c7160]/75">
                                    {nome}
                                  </h5>
                                )}
                                {precoUnico && (
                                  <p className="mt-0.5 text-xs italic font-light text-[#5c7160]/60">
                                    Preço único, para qualquer comprimento de cabelo
                                  </p>
                                )}
                              </div>
                              {temComprimentos && (
                                <div
                                  className={`hidden sm:grid flex-shrink-0 text-right ${COLUNAS_COMPRIMENTO}`}
                                >
                                  {HAIR_LENGTHS.map(({ key, label }) => (
                                    <span
                                      key={key}
                                      className="text-[10px] uppercase tracking-wider text-[#5c7160]/60"
                                    >
                                      {label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {temComprimentos && (
                            <p className="mt-2 text-xs italic font-light text-[#5c7160]/60 sm:hidden">
                              Preço conforme o comprimento do cabelo
                            </p>
                          )}
                          <ul>
                            {itens.map((svc) =>
                              hasLengthPrices(svc) ? (
                                <LinhaPorComprimento key={svc.service_id} svc={svc} />
                              ) : (
                                <LinhaServico key={svc.service_id} svc={svc} />
                              )
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 px-6 bg-[#f3f3eb] reveal">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-2xl md:text-3xl font-light text-[#5c7160] mb-6">
            Vamos marcar uma sessão?
          </h3>
          <p className="text-lg font-light text-[#5c7160]/80 mb-8">
            Estamos ansiosas para te proporcionar uma experiência de beleza
            personalizada.
          </p>
          <div className="flex justify-center">
            <BookingButton className="px-8 py-3 text-base md:text-lg">
              <span className="mr-2">Agendar Agora</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </BookingButton>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Prices;
